import type { PublicationCode } from '@/scopes/common/domain/models'
import { useDecision } from '@optimizely/react-sdk'
import { Layout } from '@sunwebgroup/ui-components/uds'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import cn from 'classnames'
import NextHead from 'next/head'
import { isEmpty } from 'ramda'
import { use, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { Custom500 } from '@/pages/500'
import { Footer } from '@/scopes/common/components'
import { CatalogueDetailsContext } from '@/scopes/common/components/CatalogueDetailsContext'
import { CatalogueContent } from '@/scopes/common/components/Footer/CatalogueContent'
import { OpenGraphTags } from '@/scopes/common/components/OpenGraphTags'
import { OPTIMIZELY_TESTS_KEYS } from '@/scopes/common/infrastructure/ab-testing/optimizely/types'
import { OptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useSendOptimizelyEvent } from '@/scopes/common/infrastructure/ab-testing/optimizely/useSendOptimizelyEvent'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { PricesActionsContextProvider } from '@/scopes/prices/components/PricesActionsContextProvider'
import { NavigationByWeeksContextProvider } from '@/scopes/prices/components/PriceTable/NavigationByWeeksContext'
import { PriceTable } from '@/scopes/prices/components/PriceTable/PriceTable'
import PromotedPrice from '@/scopes/prices/components/PromotedPrice'
import { AccoTileProductAds } from '@/scopes/product-ads/components/AccoTile'
import { getShowRecommendedAccos } from '@/scopes/products/domain/policies'
import { useSendAccoBasicDetails } from '@/scopes/products/infrastructure/analytics/useSendAccoBasicDetails'
import { PDPDependenciesProvider } from '@/scopes/products/infrastructure/dependencies'
import { pdpStore } from '@/scopes/products/infrastructure/store'
import DestinationSection from '@/scopes/products/pages/components/DestinationSection'
import { StructuredMarkup } from '@/scopes/products/pages/components/StructuredMarkup'
import { Header } from '../../common/components/Header'
import { pdpUseCases } from '../domain/useCases'
import styles from './AccommodationPage.module.css'
import AnchorMenu, { AnchorHref, AnchorTarget } from './components/AnchorMenu'
import BreadcrumbInFooter from './components/BreadcrumbInFooter/BreadcrumbInFooter'
import Description from './components/Description'
import Facilities from './components/Facilities'
import { HeroSection } from './components/HeroSection/HeroSection'
import { LinkedAccommodations } from './components/LinkedAccommodations/LinkedAccommodations'
import PDPHeading from './components/PDPHeading'
import PracticalInformation from './components/PracticalInformation'
import RecommendedAccos from './components/RecommendedAccos'
import RoomTypes from './components/RoomTypes'
import SkiAncillaries from './components/SkiAncillaries'
import UserReviews from './components/UserReviews'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
    },
  },
})

const { onAnchorTargetIntersects } = pdpUseCases({ store: pdpStore })

function hasRoomTypesData(roomTypes: any, roomTypesAdditionalInfo: any) {
  if (isEmpty(roomTypes) && !roomTypesAdditionalInfo?.trim()) {
    return false
  }
  return roomTypes?.some((roomType: any) => roomType.characteristics || !isEmpty(roomType.imageUrls))
}

function hasFacilitiesData(facilities: any) {
  return facilities && facilities.length > 0
}

function hasDestinationData(coordinates: any) {
  if (!coordinates) {
    return false
  }
  const { lat, lng } = coordinates
  return lat !== 0 || lng !== 0
}

function hasSkiAncillariesData(ski: any) {
  return !!(ski && (ski.skiPass || ski.skiMaterial || ski.skiLessons))
}

function AccomodationPageFullReact() {
  const intl = useIntl()
  const contextItemId = useCMSPageField('PDP.id')
  const roomTypes = useCMSPageField('PDP.roomTypes')
  const pdpUrl = useCMSPageField('PDP.url')
  const roomTypesAdditionalInfo = useCMSPageField('PDP.roomTypes.additionalInfoHtml')
  const facilities = useCMSPageField('PDP.facilities')
  const coordinates = useCMSPageField('PDP.coordinates')
  const metaData = useCMSPageField('PDP.MetaData')
  const skiData = useCMSPageField('PDP.SkiAncillaries')
  const [showPriceTable, setShowPriceTable] = useState(false)
  const [{ enabled: showAccoTile }] = useDecision(OPTIMIZELY_TESTS_KEYS.PROMOTED_SLIDER_PDP)
  const [noGalleryVideosDecision, noGalleryVideosDecisionReady] = useDecision(OPTIMIZELY_TESTS_KEYS.VIDEO_PDP)
  const [{ enabled: showBlackFridayCountdown }] = useDecision(OPTIMIZELY_TESTS_KEYS.BLACK_FRIDAY_COUNTDOWN)
  const [{ enabled: rephraseDiscounts }] = useDecision(OPTIMIZELY_TESTS_KEYS.REPHRASE_DISCOUNTS)
  const { setPublicationCode } = use(CatalogueDetailsContext)
  const publicationCode = useConfiguration('publicationCode')
  const showRecommendedAccos = getShowRecommendedAccos(publicationCode as PublicationCode)
  const domainUrl = useConfiguration('domainUrl')
  const brandName = useConfiguration('brandName')
  const { sendEvent: sendOptimizelyEvent } = useSendOptimizelyEvent()
  const [{ enabled: enableAlternateFlightVisualisation }] = useDecision(OPTIMIZELY_TESTS_KEYS.ALTERNATE_FLIGHT_VISUALISATION)
  useSendAccoBasicDetails()
  useEffect(() => {
    sendOptimizelyEvent('pdp_reached')
  }, [])

  const showRoomTypesSection = hasRoomTypesData(roomTypes, roomTypesAdditionalInfo)
  const showFacilitiesSection = hasFacilitiesData(facilities)
  const showDestinationSection = hasDestinationData(coordinates)
  const showSkiAncillariesSection = hasSkiAncillariesData(skiData)

  const promotedPriceClick = () => {
    setShowPriceTable(true)
  }

  useEffect(() => {
    if (showPriceTable) {
      const selector = document.querySelector('#main-section')

      if (selector) {
        const ua = navigator.userAgent.toLowerCase()
        const isFirefox = ua.includes('firefox')
        const isIOS = /iphone|ipod/.test(ua) || ua.includes('macintosh') // Recognizes: iPhone, iPod and iPad & macOS systems
        const top = selector.getBoundingClientRect().top + window.scrollY

        if (isFirefox || isIOS) {
          window.scrollTo({ top })
        }
        else {
          window.scrollTo({ top, behavior: 'smooth' })
        }
      }
    }
  }, [showPriceTable])

  useLayoutEffect(() => {
    const url = window.location.search
    const params = new URLSearchParams(url.toLowerCase())
    const showPriceTableParam = params.get('focuspricetable')
    if ((showPriceTableParam && showPriceTableParam?.toLowerCase() === 'true') || params.get('selectedpriceid')) {
      promotedPriceClick()
    }
  }, [])

  useEffect(() => {
    setPublicationCode(publicationCode)
  }, [publicationCode])

  const optimizelyFlagValue = useMemo(
    () => ({
      enableAlternateFlightVisualisation,
      noGalleryVideosDecisionReady,
      noGalleryVideosDecision: noGalleryVideosDecision?.enabled ?? false,
      showBlackFridayCountdown,
      rephraseDiscounts,
    }),
    [noGalleryVideosDecisionReady, noGalleryVideosDecision?.enabled, showBlackFridayCountdown, enableAlternateFlightVisualisation, rephraseDiscounts],
  )

  return (
    <div className={styles.wrapper}>
      <NextHead>
        <meta name="viewport" content="user-scalable=no,initial-scale=1.0,maximum-scale=1.0" />
      </NextHead>
      <OpenGraphTags
        title={metaData.title}
        description={metaData.description}
        image={{ url: metaData.image }}
        url={`${domainUrl}${pdpUrl}`}
      />
      <StructuredMarkup />
      <PDPDependenciesProvider dependencies={{ pdpStore }}>
        <OptimizelyFlagContext
          value={optimizelyFlagValue}
        >
          <PricesActionsContextProvider>
            <QueryClientProvider client={queryClient}>
              <Header />
              <main id="main-content" tabIndex={-1}>
                <NewRelicErrorBoundary scope="acco-page" fallback={<Custom500 brand={brandName} />}>
                  <HeroSection />
                  <PDPHeading />
                  <div id="main-section">
                    {!showPriceTable && (
                      <>
                        <Layout>
                          <div className={styles.layoutWithSidebar}>
                            <div className={styles.detailSection}>
                              <AnchorMenu
                                showRoomTypesMenu={showRoomTypesSection}
                                showFacilitiesMenu={showFacilitiesSection}
                                showDestinationMenu={showDestinationSection}
                                showSkiAncillariesMenu={showSkiAncillariesSection}
                              />
                              <div className={styles.detailSectionContent}>
                                <AnchorTarget
                                  anchorHref={AnchorHref.General}
                                  onAnchorIntersect={onAnchorTargetIntersects}
                                >
                                  <Description />
                                </AnchorTarget>
                                {showRoomTypesSection && (
                                  <AnchorTarget
                                    anchorHref={AnchorHref.Roomtypes}
                                    onAnchorIntersect={onAnchorTargetIntersects}
                                  >
                                    <RoomTypes />
                                  </AnchorTarget>
                                )}
                                {showFacilitiesSection && (
                                  <AnchorTarget
                                    anchorHref={AnchorHref.Facilities}
                                    onAnchorIntersect={onAnchorTargetIntersects}
                                  >
                                    <Facilities />
                                  </AnchorTarget>
                                )}
                                <AnchorTarget
                                  anchorHref={AnchorHref.PracticalInfo}
                                  onAnchorIntersect={onAnchorTargetIntersects}
                                >
                                  <PracticalInformation />
                                </AnchorTarget>
                                <AnchorTarget
                                  anchorHref={AnchorHref.Reviews}
                                  onAnchorIntersect={onAnchorTargetIntersects}
                                >
                                  <UserReviews />
                                </AnchorTarget>
                                {showDestinationSection && (
                                  <AnchorTarget
                                    anchorHref={AnchorHref.Destination}
                                    onAnchorIntersect={onAnchorTargetIntersects}
                                  >
                                    <DestinationSection />
                                  </AnchorTarget>
                                )}
                                {showSkiAncillariesSection && (
                                  <AnchorTarget
                                    anchorHref={AnchorHref.SkiAncillaries}
                                    onAnchorIntersect={onAnchorTargetIntersects}
                                  >
                                    <SkiAncillaries />
                                  </AnchorTarget>
                                )}
                              </div>
                            </div>
                            <div className={styles.promotedPriceSection}>
                              <PromotedPrice onClick={promotedPriceClick} />
                            </div>
                          </div>
                        </Layout>
                        {showAccoTile && (
                          <div className={styles.recommendedAccosLayoutLegacy}>
                            <Layout>
                              <AccoTileProductAds
                                contextItemId={contextItemId}
                                headerTitle={intl.formatMessage({ id: 'search_result_product_ads_title' })}
                                infoLabel={intl.formatMessage({ id: 'search_result_product_ads_label' })}
                                infoContent={intl.formatMessage({ id: 'search_result_product_ads_content' })}
                              />
                            </Layout>
                          </div>
                        )}
                        {showRecommendedAccos && (
                          <Layout overflowXHidden variant="fullWidth">
                            <Layout>
                              <RecommendedAccos />
                            </Layout>
                          </Layout>
                        )}
                        <Layout className={styles.linkedAccosLayout}>
                          <LinkedAccommodations />
                        </Layout>
                      </>
                    )}
                    <Layout
                      className={cn(styles.priceTableSection, {
                        [styles.priceTableSectionVisible]: showPriceTable,
                      })}
                    >
                      <NavigationByWeeksContextProvider>
                        <PriceTable onClickBack={() => setShowPriceTable(false)} />
                      </NavigationByWeeksContextProvider>
                    </Layout>
                  </div>
                </NewRelicErrorBoundary>
              </main>
              <Footer>
                <BreadcrumbInFooter />
                <CatalogueContent />
              </Footer>
              {process.env.NODE_ENV === 'development' && <ReactQueryDevtools client={queryClient} />}
            </QueryClientProvider>
          </PricesActionsContextProvider>
        </OptimizelyFlagContext>
      </PDPDependenciesProvider>
    </div>
  )
}

export default AccomodationPageFullReact
